import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CSVLink } from "react-csv";
import {
  Box, Button, Card, CardContent, Chip, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import api from "../../../services/api";
import LoadingSkeleton from "../../../components/common/LoadingSkeleton";
import EmptyState from "../../../components/admin/EmptyState";
import useToastStore from "../../../store/toastStore";

const exportCsvHeaders = [
  { label: "요청 번호", key: "marketLocationId" },
  { label: "완료일", key: "completedDate" },
  { label: "지역", key: "region" },
  { label: "작업자", key: "collectorName" },
  { label: "품목 코드", key: "itemCode" },
  { label: "품목명", key: "itemName" },
  { label: "수량", key: "quantity" },
  { label: "수출 가능", key: "exportAvailable" },
  { label: "예외 품목", key: "exceptionItem" },
  { label: "검수 메모", key: "inspectionMemo" },
  { label: "승인자", key: "approvedByName" },
  { label: "승인 시각", key: "approvedAt" },
];

function requestSearchUrl(marketLocationId) {
  const search = new URLSearchParams({
    q: String(marketLocationId),
    fromDate: "",
    toDate: "",
  });
  return `/admin/dream/requests?${search.toString()}`;
}

export default function MarketExportItemsTab() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const showToast = useToastStore((s) => s.showToast);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/admin/export-items");
      setItems(res.data || []);
    } catch (err) {
      console.error("수출 품목 조회 실패:", err);
      showToast("수출 품목 목록을 불러오지 못했습니다.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filtered = items.filter((item) => {
    const text = [
      item.marketLocationId, item.itemCode, item.itemName, item.region,
      item.collectorName, item.approvedByName,
    ].join(" ").toLowerCase();
    return text.includes(query.toLowerCase());
  });
  const csvData = filtered.map((item) => ({
    marketLocationId: item.marketLocationId || "",
    completedDate: item.completedDate || "",
    region: item.region || "",
    collectorName: item.collectorName || "",
    itemCode: item.itemCode || "",
    itemName: item.itemName || "",
    quantity: item.quantity ?? "",
    exportAvailable: item.exportAvailable === false ? "N" : "Y",
    exceptionItem: item.exceptionItem ? "Y" : "N",
    inspectionMemo: item.inspectionMemo || "",
    approvedByName: item.approvedByName || "",
    approvedAt: item.approvedAt || "",
  }));

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>수출 품목 관리</Typography>
            <Typography variant="body2" color="text.secondary">
              관리자 승인 시 저장된 실제 수거 품목 코드와 수량을 조회합니다.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <TextField size="small" label="검색" value={query} onChange={(e) => setQuery(e.target.value)} />
            {csvData.length > 0 ? (
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                component={CSVLink}
                data={csvData}
                headers={exportCsvHeaders}
                filename="dream_export_items.csv"
              >
                CSV
              </Button>
            ) : (
              <Button variant="outlined" startIcon={<DownloadIcon />} disabled>CSV</Button>
            )}
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchItems} disabled={loading}>
              새로고침
            </Button>
          </Box>
        </CardContent>
      </Card>

      {loading ? <LoadingSkeleton rows={5} /> : filtered.length === 0 ? <EmptyState message="수출 품목 기록이 없습니다." /> : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>요청 번호</TableCell>
                <TableCell>완료일</TableCell>
                <TableCell>지역</TableCell>
                <TableCell>작업자</TableCell>
                <TableCell>품목 코드</TableCell>
                <TableCell>품목명</TableCell>
                <TableCell align="right">수량</TableCell>
                <TableCell>승인자</TableCell>
                <TableCell>승인 시각</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((item) => (
                <TableRow key={item.exportItemId} hover>
                  <TableCell>
                    {item.marketLocationId ? (
                      <Link
                        to={requestSearchUrl(item.marketLocationId)}
                        style={{ color: "inherit", textDecoration: "none", fontWeight: 600 }}
                      >
                        #{item.marketLocationId}
                      </Link>
                    ) : "-"}
                  </TableCell>
                  <TableCell>{item.completedDate || "-"}</TableCell>
                  <TableCell>{item.region || "-"}</TableCell>
                  <TableCell>{item.collectorName || "-"}</TableCell>
                  <TableCell><Chip size="small" label={item.itemCode} /></TableCell>
                  <TableCell>{item.itemName}</TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell>{item.approvedByName || "-"}</TableCell>
                  <TableCell>{item.approvedAt ? new Date(item.approvedAt).toLocaleString("ko-KR") : "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
